import styled, { css } from 'styled-components'
import { ButtonUnstyled } from 'components/Button'
import { TransitionStatus } from 'react-transition-group/Transition'

type TransitionProps = {
  state: TransitionStatus
}

const closed = {
  width: 144,
  height: 32,
} as const

const open = {
  width: 290,
  height: 212,
}

const isOpen = (state: TransitionStatus) =>
  state === 'entered' || state === 'entering'

export const Container = styled.div<TransitionProps>`
  position: relative;
`

export const Panel = styled.div<TransitionProps>`
  background-color: rgba(0, 0, 0, 0.04);
  border: 1px solid;
  border-color: rgba(0, 0, 0, 0);
  border-radius: 8px;
  box-shadow: 0 7px 25px 0 rgba(0, 0, 0, 0);
  height: ${closed.height}px;
  width: ${closed.width}px;
  transition: ${({ theme }) =>
    `all ${theme.transitions.duration[2]} ${theme.transitions.timing.easeOutExponential}`};

  ${({ theme, state }) =>
    isOpen(state) &&
    css`
      background-color: ${theme.colors.background.light.base};
      border-color: ${theme.colors.border.dark.ultraHigh};
      border-radius: 12px;
      box-shadow: ${theme.shadows.boxShadow.elevated};
      width: ${open.width}px;
      height: ${open.height}px;
    `}
`

export const Content = styled.div<TransitionProps>`
  display: flex;
  flex-direction: column;
  padding: 8px;
  position: absolute;
  top: 0;
  left: 0;
  height: ${closed.height}px;
  width: ${open.width}px;
  overflow: hidden;
  transition: ${({ theme }) =>
    `all ${theme.transitions.duration[2]} ${theme.transitions.timing.easeOutExponential}`};

  ${({ state }) =>
    isOpen(state) &&
    css`
      width: ${open.width}px;
      height: ${open.height}px;
      padding: 20px;
    `}
`

export const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`

export const Title = styled.div<TransitionProps>`
  font-size: 0.75rem;
  font-family: ${({ theme }) => theme.fonts.headings};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.dark.secondary};
  line-height: 1;
  margin-right: 4px;
  transition: ${({ theme }) =>
    `all ${theme.transitions.duration[2]} ${theme.transitions.timing.easeOut}`};

  ${({ state, theme }) =>
    isOpen(state) &&
    css`
      font-size: 1rem;
      font-weight: ${theme.fontWeights.medium};
      color: ${theme.colors.text.dark.base};
    `}
`

export const CloseButton = styled(ButtonUnstyled)<TransitionProps>`
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.background.tertiary[2]};
  border-radius: 50%;
  width: 18px;
  height: 18px;
  padding-top: 1px; /* stylelint-disable-line scales/space */
  margin-left: auto;
  display: ${({ state }) => (isOpen(state) ? 'block' : 'none')};
`

export const OpenButton = styled.div<TransitionProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${closed.width}px;
  height: ${closed.height}px;
  cursor: pointer;
  display: ${({ state }) => (state === 'exited' ? 'block' : 'none')};
`

export const CopyContainer = styled.div<TransitionProps>`
  margin-bottom: 8px;
  opacity: 0;
  transition: ${({ theme }) =>
    `all ${theme.transitions.duration[2]} ${theme.transitions.timing.easeOutExponential}`};

  ${({ state }) =>
    isOpen(state) &&
    css`
      opacity: 1;
    `}
`

export const Copy = styled.div`
  font-size: 0.75rem;
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.dark.secondary};
  line-height: 1.6;
  margin-bottom: 4px;
`

export const Footer = styled.div<TransitionProps>`
  display: flex;
  justify-content: space-between;
  opacity: 0;
  transition: ${({ theme }) =>
    `all ${theme.transitions.duration[2]} ${theme.transitions.timing.easeOutExponential}`};

  ${({ state }) =>
    isOpen(state) &&
    css`
      opacity: 1;
    `}
`
